<script setup lang="ts">
import BackstagePopoverPanel from '@/components/common/BackstagePopoverPanel.vue';
import { globalConstants } from '@/constants/global.constants';
import type { FaqSectionModel } from '@/models/faq-section.model';
import { RouteNames } from '@/router';
import { getLocalizedValue } from '@onrunning/backstage-models';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useLocale } from '../../composables/locale';
import { FaqsService } from '../../services/faqs.service';
import HeaderTriggerButton from './HeaderTriggerButton.vue';

const { userLanguage } = useLocale();
const faqsService = new FaqsService();
const rawSections = ref<FaqSectionModel[]>([]);

const faqSections = computed(() =>
  rawSections.value.map((sectionModel) => {
    const section = {
      id: sectionModel.externalId,
      title: getLocalizedValue(sectionModel.title, userLanguage.value, globalConstants.fallBackLocale) ?? '',
    };

    return section;
  }),
);

const router = useRouter();

const goTo = (sectionId: string) => {
  router.push({ name: RouteNames.FaqsSection, params: { id: sectionId } });
};

const fetchFaqSections = async () => {
  const result = await faqsService.getFaqSections();
  if (result.isValid() && result.model) {
    rawSections.value = result.model;
  }
};

onMounted(() => {
  fetchFaqSections();
});

const showFaqPanel = defineModel<boolean>({ required: true });
const onFaqTrigger = () => {
  showFaqPanel.value = true;
};
</script>

<template>
  <div class="faq-additional-access" data-cy="faq-additional-access">
    <BackstagePopoverPanel v-model="showFaqPanel" @mouseleave="showFaqPanel = false">
      <template #trigger>
        <HeaderTriggerButton
          dataCy="question-mark-icon"
          iconName="question-mark"
          @click="onFaqTrigger"
          @mouseover="onFaqTrigger" />
      </template>
      <template #title> FAQs </template>
      <template #body>
        <div class="faq-container" v-if="faqSections.length" data-cy="faq-container">
          <span
            v-for="(section, index) in faqSections"
            :key="section.id"
            class="header-faq-menu-item"
            :class="{ 'bottom-divider': index !== faqSections.length - 1 }"
            data-cy="header-faq-menu-item"
            @click="goTo(section.id)">
            {{ section.title }}
          </span>
        </div>
        <div v-else class="loader"></div>
      </template>
    </BackstagePopoverPanel>
  </div>
</template>

<style scoped lang="scss">
.faq-container {
  display: flex;
  flex-direction: column;
  gap: 0rem;

  .header-faq-menu-item {
    padding: 0.8rem;
    cursor: pointer;
    font-size: 14px;
    color: var(--primary-text-color);
    background-color: var(--shade-1);
    &:hover {
      background-color: var(--shade-2);
    }
  }
}

.bottom-divider {
  border-bottom: 1px solid var(--shade-3);
}
</style>
